/**
 * WHAT A REP IS TOLD WHEN AN ASSET DOES NOT ATTACH — pure, no imports.
 *
 * ⚠️ THE DEFECT THIS EXISTS TO PREVENT. The picker used to write one sentence
 * for every failure, and that sentence concatenated the thrown message with
 * SR's standing Canva policy:
 *
 *   `“${item.name}” was not attached. ${err.message} Nothing was stored,
 *    because a link to Canva would expire on the build. Open the design in
 *    Canva, export a PNG, then use “Upload from this device”.`
 *
 * On 2026-09-21 `err.message` was HubSpot's own
 * `ProduceFailedException ... topic 'file-manager-model-create-update-delete-topic'`,
 * so a rep was handed a vendor stack trace followed by an instruction that
 * would not have helped: the Canva rule is correct standing policy and it was
 * NOT what happened. HubSpot's storage write failed. An error message is a
 * claim about a cause, and that one was false.
 *
 * So the two states are composed here, separately, and never share wording:
 *
 *   POLICY    — SR will not store this. Decided BEFORE any attempt. The rep is
 *               told what to do instead, and no retry is offered.
 *   TRANSPORT — the storage write failed. Nothing is wrong with the design, so
 *               "try again" is the advice and "go export a PNG" never is.
 *
 * Pure and standalone so `verify-studio.mjs` can assert the wording rather
 * than trust it, exactly as `canvaGap.js` is.
 */

/** Number of attempts the upload path makes. Mirrors ownedAsset#MAX_ATTEMPTS. */
export const ATTEMPTS = 4

/**
 * @param {string} name     the asset the rep tried to attach
 * @param {{kind?:string, message?:string, correlationId?:string}} err
 * @returns {{state:'policy'|'transport', headline:string, fix:string, ref:string}}
 */
export function attachFailure(name, err) {
  const label = name ? `“${name}”` : 'That design'
  const ref = err?.correlationId || ''
  const kind = err?.kind || 'transport'

  /* POLICY — nothing was attempted, and nothing should be. */
  if (kind === 'canva-link') {
    return {
      state: 'policy',
      headline: `${label} is not attached, because a Canva link cannot go on a build.`,
      fix: 'A Canva link expires, so the record would stop resolving on its own. Open the design in Canva, export a PNG, then use “Upload from this device”.',
      ref: '',
    }
  }
  if (kind === 'policy') {
    return {
      state: 'policy',
      headline: `${label} is not attached.`,
      fix: `${err?.message || 'File Manager will not take that file.'} Export it as a PNG under 6MB and use “Upload from this device”.`,
      ref: '',
    }
  }

  /* TRANSPORT — something on the way to storage failed. Retrying is the fix. */
  if (kind === 'source') {
    return {
      state: 'transport',
      headline: `${label} is not attached yet. Its preview could not be read from Canva's host.`,
      fix: 'This is Canva, not your design and not SR storage. Try again in a minute, or export a PNG and use “Upload from this device”.',
      ref,
    }
  }
  if (kind === 'config') {
    return {
      state: 'transport',
      headline: `${label} is not attached yet. File Manager is not configured on this deployment.`,
      fix: 'Nothing can be stored until that is set. This is not a rejected file and retrying will not change it. Tell Brian before you keep going.',
      ref,
    }
  }
  return {
    state: 'transport',
    headline: `${label} is not attached yet. HubSpot file storage did not accept it after ${ATTEMPTS} attempts.`,
    fix: 'This is a storage outage on their side, not a problem with your design. Try again in a minute, or use “Upload from this device” if it keeps failing.',
    ref,
  }
}

/** The line that carries the reference. Kept separate so it can be omitted. */
export function refLine(ref) {
  return ref ? `Quote reference ${ref} if you report this.` : ''
}
